import { CurrencyIcon, MMButton, ModalContentSurface, ModalFooter, ModalSubtitle, ModalTitle } from '@components'
import cn from '@integrations/classnames'
import { useContext, useState } from '@integrations/react'
import { uiSize } from '@utils'
import { useAccountDeleteQry } from 'mmfintech-backend-api'
import type { AccountBalanceResponse } from 'mmfintech-backend-api/types'
import { GlobalContext, tr } from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import styles from '../AccountCreateModal/AccountCreateModal.module.scss'

interface Props {
  account: AccountBalanceResponse
  onSuccess: () => void
  onContactSupport: () => void
}

/**
 * Warning before an account is closed (deactivated). The backend decides whether closing is
 * allowed (negative balance, only account, IBAN attached); when it refuses, the dialog switches
 * to a "contact support" state instead of surfacing the raw error.
 */
function AccountCloseModal({ account, onSuccess, onContactSupport }: Props) {
  const { modalHide } = useContext(GlobalContext)
  const [blocked, setBlocked] = useState(false)

  const { id: accountId, name, currencyCode } = account || {}
  const { deleteAccount, deleteAccountLoading } = useAccountDeleteQry()

  function handleConfirm() {
    deleteAccount(
      accountId,
      () => {
        modalHide()
        onSuccess()
      },
      () => setBlocked(true)
    )
  }

  function handleContactSupport() {
    modalHide()
    onContactSupport()
  }

  const consequences = [
    tr(
      'FRONTEND.ACCOUNTS.CLOSE.CONSEQUENCE_OPERATIONS',
      'You won’t be able to deposit to, withdraw from, exchange or pay out from this account.'
    ),
    tr(
      'FRONTEND.ACCOUNTS.CLOSE.CONSEQUENCE_IRREVERSIBLE',
      'Closing can’t be undone. To use {{CURRENCY}} again, open a new account.',
      { CURRENCY: currencyCode }
    ),
    tr('FRONTEND.ACCOUNTS.CLOSE.CONSEQUENCE_HISTORY', 'Its past transactions stay available in Transactions.')
  ]

  return (
    <ModalContentSurface className={cn('account-close-wrapper')} role='alertdialog' data-test='close-account-modal'>
      <div className={cn(styles['account-create-header'], 'account-create-header')}>
        <ModalTitle className={cn(styles['account-create-title'], 'account-create-title')}>
          {tr('FRONTEND.ACCOUNTS.CLOSE.TITLE', 'Close this account?')}
        </ModalTitle>
        <ModalSubtitle className={cn(styles['account-close-identity'], 'account-close-identity')}>
          <CurrencyIcon currency={currencyCode} size={uiSize(20)} circle />
          <span>
            <strong>{name}</strong> · {currencyCode} ·{' '}
            {tr('FRONTEND.ACCOUNTS.CLOSE.ID', 'ID {{ID}}', { ID: accountId })}
          </span>
        </ModalSubtitle>
      </div>

      <div className={cn(styles['account-close-body'], 'account-close-body')}>
        {blocked ? (
          <ErrorDisplay
            error={tr(
              'FRONTEND.ACCOUNTS.CLOSE.NOT_ALLOWED',
              'This account cannot be closed. Please contact support for more info'
            )}
            data-test='close-account-blocked'
          />
        ) : (
          <ul className={cn(styles['account-close-consequences'], 'account-close-consequences')}>
            {consequences.map((text) => (
              <li key={text}>{text}</li>
            ))}
          </ul>
        )}
      </div>

      <ModalFooter>
        <MMButton
          type='button'
          variant='secondary'
          caption={tr('FRONTEND.BUTTONS.CANCEL', 'Cancel')}
          onClick={modalHide}
          disabled={deleteAccountLoading}
          data-test='button-cancel'
        />
        {blocked ? (
          <MMButton
            type='button'
            variant='primary'
            caption={tr('FRONTEND.ACCOUNTS.CLOSE.CONTACT_SUPPORT', 'Contact support')}
            onClick={handleContactSupport}
            data-test='button-contact-support'
          />
        ) : (
          <MMButton
            type='button'
            variant='delete'
            caption={tr('FRONTEND.ACCOUNTS.CLOSE.BUTTON', 'Close account')}
            onClick={handleConfirm}
            loading={deleteAccountLoading}
            data-test='button-submit'
          />
        )}
      </ModalFooter>
    </ModalContentSurface>
  )
}

export default AccountCloseModal
